(function () {
  "use strict";

  var data = window.SensoriumQuizData;
  if (!data) return;

  var quizzes = data.quizzes;
  var senses = data.senses;
  var senseOrder = data.senseOrder;
  var categories = data.categories;

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function readCompleted() {
    try {
      return JSON.parse(
        window.localStorage.getItem("sensorium-completed-quizzes") || "[]",
      );
    } catch (error) {
      return [];
    }
  }

  function writeCompleted(completed) {
    try {
      window.localStorage.setItem(
        "sensorium-completed-quizzes",
        JSON.stringify(completed),
      );
    } catch (error) {
      /* Browser storage can be disabled; the quiz still works without it. */
    }
  }

  function readPendingResults() {
    try {
      var pending = JSON.parse(
        window.localStorage.getItem("sensorium-pending-quiz-results") || "[]",
      );
      return Array.isArray(pending) ? pending : [];
    } catch (error) {
      return [];
    }
  }

  function writePendingResults(results) {
    try {
      window.localStorage.setItem(
        "sensorium-pending-quiz-results",
        JSON.stringify(results),
      );
    } catch (error) {
      /* A guest can still view the result when browser storage is disabled. */
    }
  }

  function createClientResultId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return "quiz_" + window.crypto.randomUUID();
    }
    return (
      "quiz_" +
      Date.now().toString(36) +
      "_" +
      Math.random().toString(36).slice(2, 12)
    );
  }

  function findQuiz(id) {
    return quizzes.find(function (quiz) {
      return quiz.id === id;
    });
  }

  function scoresFor(answers) {
    var scores = {};
    senseOrder.forEach(function (sense) {
      scores[sense] = 0;
    });
    answers.forEach(function (answer) {
      if (answer) scores[answer] += 1;
    });
    return scores;
  }

  function resultFor(answers) {
    var scores = scoresFor(answers);
    var ranked = senseOrder.slice().sort(function (a, b) {
      var difference = scores[b] - scores[a];
      return difference || senseOrder.indexOf(a) - senseOrder.indexOf(b);
    });
    var lowest = Math.min.apply(
      null,
      senseOrder.map(function (sense) {
        return scores[sense];
      }),
    );
    return {
      scores: scores,
      primary: ranked[0],
      quiet: senseOrder.filter(function (sense) {
        return scores[sense] === lowest;
      }),
    };
  }

  function initialize(root) {
    if (!root || root.dataset.sqReady === "true") return;
    root.dataset.sqReady = "true";

    var state = {
      activeQuiz: null,
      phase: "home",
      questionIndex: 0,
      answers: [],
      category: "All",
      completed: readCompleted(),
      shareLabel: "Share my result",
      membership: {
        status: "loading",
        user: null,
        csrf: "",
        resultCount: 0,
      },
      currentResultRecord: null,
      saveStatus: "idle",
      saveMessage: "",
    };

    var membershipApi = root.dataset.membershipApi || "";
    var joinUrl = root.dataset.joinUrl || "https://sixsense.sensoriumliving.com/#signup";
    var loginUrl = root.dataset.loginUrl || "https://sixsense.sensoriumliving.com/#login";
    var memberAreaUrl = root.dataset.memberAreaUrl || "/pages/member-area";
    var membershipRefreshPromise = null;

    function apiUrl(action) {
      return membershipApi + (membershipApi.indexOf("?") === -1 ? "?" : "&") + "action=" + encodeURIComponent(action);
    }

    function apiRequest(action, options) {
      options = options || {};
      var request = {
        method: options.method || "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      };
      if (options.body) {
        request.headers["Content-Type"] = "application/json";
        request.headers["X-Membership-CSRF"] = state.membership.csrf;
        request.body = JSON.stringify(options.body);
      }
      return window.fetch(apiUrl(action), request).then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (payload) {
            if (!response.ok || payload.ok === false) {
              var error = new Error(payload.message || "The member service is temporarily unavailable.");
              error.status = response.status;
              throw error;
            }
            return payload;
          });
      });
    }

    function memberName(user) {
      if (!user) return "";
      return (
        [user.first_name, user.last_name].filter(Boolean).join(" ") ||
        user.username ||
        user.email ||
        "Member"
      );
    }

    function queueResult(record) {
      var pending = readPendingResults();
      var exists = pending.some(function (item) {
        return item.client_result_id === record.client_result_id;
      });
      if (!exists) {
        pending.push(record);
        writePendingResults(pending);
      }
    }

    function removePendingResult(clientResultId) {
      writePendingResults(
        readPendingResults().filter(function (item) {
          return item.client_result_id !== clientResultId;
        }),
      );
    }

    function buildResultRecord() {
      var quiz = state.activeQuiz;
      var result = resultFor(state.answers);
      var profile = senses[result.primary];
      return {
        client_result_id: createClientResultId(),
        quiz_id: quiz.id,
        quiz_title: quiz.title,
        quiz_short_title: quiz.shortTitle,
        completed_at: new Date().toISOString(),
        answers: quiz.questions.map(function (question, index) {
          var selectedSense = state.answers[index];
          var selectedOption = question.options.find(function (option) {
            return option.sense === selectedSense;
          });
          return {
            question: question.prompt,
            selected_label: selectedOption ? selectedOption.label : "",
            selected_sense: selectedSense,
          };
        }),
        scores: result.scores,
        primary_sense: result.primary,
        quiet_senses: result.quiet,
        result_intro: quiz.resultIntros[result.primary],
        result_essence: profile.essence,
        ritual: profile.ritual,
        affirmation: profile.affirmation,
      };
    }

    function loadMemberResults() {
      return apiRequest("quiz-results").then(function (payload) {
        var results = Array.isArray(payload.results) ? payload.results : [];
        state.membership.resultCount = results.length;
        results.forEach(function (result) {
          if (result.quiz_id && state.completed.indexOf(result.quiz_id) === -1) {
            state.completed.push(result.quiz_id);
          }
        });
        writeCompleted(state.completed);
      });
    }

    function syncPendingResults() {
      var pending = readPendingResults();
      if (!pending.length || !state.membership.user || !state.membership.csrf) {
        return Promise.resolve();
      }

      var remaining = [];
      return pending
        .reduce(function (chain, record) {
          return chain.then(function () {
            return apiRequest("save-quiz-result", { method: "POST", body: record })
              .then(function () {
                if (
                  state.currentResultRecord &&
                  state.currentResultRecord.client_result_id === record.client_result_id
                ) {
                  state.saveStatus = "saved";
                  state.saveMessage = "Saved to your member account.";
                }
              })
              .catch(function (error) {
                remaining.push(record);
                if (
                  state.currentResultRecord &&
                  state.currentResultRecord.client_result_id === record.client_result_id
                ) {
                  state.saveStatus = "error";
                  state.saveMessage = error.message;
                }
              });
          });
        }, Promise.resolve())
        .then(function () {
          writePendingResults(remaining);
        });
    }

    function refreshMembership() {
      if (!membershipApi || !window.fetch) {
        state.membership.status = "unavailable";
        render();
        return Promise.resolve();
      }
      if (membershipRefreshPromise) return membershipRefreshPromise;

      membershipRefreshPromise = apiRequest("plans")
        .then(function (plans) {
          state.membership.csrf = plans.csrf || "";
          return apiRequest("session");
        })
        .then(function (session) {
          state.membership.user = session.authenticated ? session.user : null;
          state.membership.status = session.authenticated ? "member" : "guest";
          if (!session.authenticated) return null;
          return syncPendingResults().then(loadMemberResults);
        })
        .catch(function () {
          state.membership.status = "unavailable";
          state.membership.user = null;
        })
        .then(function () {
          membershipRefreshPromise = null;
          render();
        });
      return membershipRefreshPromise;
    }

    function saveCurrentResult() {
      if (!state.currentResultRecord) return;
      var record = state.currentResultRecord;
      if (state.membership.status !== "member" || !state.membership.csrf) {
        queueResult(record);
        state.saveStatus = "queued";
        state.saveMessage = "Join or sign in and this result will be saved automatically.";
        render();
        return;
      }

      state.saveStatus = "saving";
      state.saveMessage = "Saving your complete result…";
      render();
      apiRequest("save-quiz-result", {
        method: "POST",
        body: record,
      })
        .then(function (payload) {
          removePendingResult(record.client_result_id);
          if (
            state.currentResultRecord &&
            state.currentResultRecord.client_result_id === record.client_result_id
          ) {
            state.saveStatus = "saved";
            state.saveMessage = "Saved to your member account.";
          }
          if (payload.saved !== false) state.membership.resultCount += 1;
          render();
        })
        .catch(function (error) {
          queueResult(record);
          if (error.status === 401) {
            state.membership.status = "guest";
            state.membership.user = null;
            if (
              state.currentResultRecord &&
              state.currentResultRecord.client_result_id === record.client_result_id
            ) {
              state.saveStatus = "queued";
              state.saveMessage = "Sign in and this result will be saved automatically.";
            }
          } else {
            if (
              state.currentResultRecord &&
              state.currentResultRecord.client_result_id === record.client_result_id
            ) {
              state.saveStatus = "error";
              state.saveMessage = error.message;
            }
          }
          render();
        });
    }

    function goToTop(smooth) {
      var top = root.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top: top, behavior: smooth ? "smooth" : "auto" });
    }

    function openQuiz(id) {
      var quiz = findQuiz(id);
      if (!quiz) return;
      state.activeQuiz = quiz;
      state.phase = "intro";
      state.questionIndex = 0;
      state.answers = quiz.questions.map(function () {
        return null;
      });
      state.shareLabel = "Share my result";
      state.currentResultRecord = null;
      state.saveStatus = "idle";
      state.saveMessage = "";
      render();
      goToTop(true);
    }

    function goHome() {
      state.activeQuiz = null;
      state.phase = "home";
      state.questionIndex = 0;
      state.answers = [];
      state.currentResultRecord = null;
      state.saveStatus = "idle";
      state.saveMessage = "";
      render();
      goToTop(true);
    }

    function symbolRing() {
      return (
        '<div class="sq-orbit" aria-hidden="true">' +
        senseOrder
          .map(function (sense, index) {
            return (
              '<span class="sq-orbit__sense sq-orbit__sense--' +
              (index + 1) +
              '"><i>' +
              senses[sense].icon +
              "</i>" +
              senses[sense].name +
              "</span>"
            );
          })
          .join("") +
        '<div class="sq-orbit__center"><span>THE</span><strong>SIX</strong><span>SENSES</span></div>' +
        "</div>"
      );
    }

    function memberBar() {
      if (state.membership.status === "member") {
        return (
          '<aside class="sq-member-strip sq-member-strip--member"><div><span>Free member profile</span><p>Welcome, ' +
          escapeHtml(memberName(state.membership.user)) +
          ". Your completed reflections follow you across devices.</p></div><div><span>" +
          state.membership.resultCount +
          ' saved result' +
          (state.membership.resultCount === 1 ? "" : "s") +
          '</span><a href="' +
          escapeHtml(memberAreaUrl) +
          '">Review my results →</a></div></aside>'
        );
      }

      if (state.membership.status === "loading") {
        return '<aside class="sq-member-strip"><div><span>Free member profile</span><p>Checking your member account…</p></div></aside>';
      }

      return (
        '<aside class="sq-member-strip"><div><span>Save every reflection</span><p>Join free to keep your complete quiz answers, results, and rituals in your private member area.</p></div><div class="sq-member-strip__actions"><a class="sq-member-link sq-member-link--primary" href="' +
        escapeHtml(joinUrl) +
        '" target="_blank" rel="noopener">Join free</a><a class="sq-member-link" href="' +
        escapeHtml(loginUrl) +
        '" target="_blank" rel="noopener">Sign in</a></div></aside>'
      );
    }

    function homeView() {
      var heading = root.dataset.heading || "Come back to your senses.";
      var subheading =
        root.dataset.subheading ||
        "A series of complimentary reflections for discovering how you perceive, restore, and move through the world.";
      var visibleQuizzes =
        state.category === "All"
          ? quizzes
          : quizzes.filter(function (quiz) {
              return quiz.category === state.category;
            });

      var marquee = senseOrder
        .concat(senseOrder)
        .map(function (sense, index) {
          return (
            '<span><b>' +
            senses[sense].icon +
            "</b> " +
            senses[sense].name +
            "</span>"
          );
        })
        .join("");

      var cards = visibleQuizzes
        .map(function (quiz) {
          var sense = senseOrder[(Number(quiz.number) - 1) % senseOrder.length];
          var profile = senses[sense];
          var completed = state.completed.indexOf(quiz.id) !== -1;
          return (
            '<article class="sq-card' +
            (quiz.featured ? " sq-card--featured" : "") +
            '" style="--sq-accent:' +
            profile.color +
            '">' +
            '<div class="sq-card__top"><span>' +
            quiz.number +
            "</span><span>" +
            (completed ? "Completed ✓" : escapeHtml(quiz.category)) +
            "</span></div>" +
            '<div class="sq-card__symbol"><span>' +
            profile.icon +
            "</span></div>" +
            '<div class="sq-card__copy"><small>' +
            escapeHtml(quiz.invitation) +
            "</small><h3>" +
            escapeHtml(quiz.title) +
            "</h3><p>" +
            escapeHtml(quiz.description) +
            "</p></div>" +
            '<button type="button" data-sq-action="open" data-quiz-id="' +
            escapeHtml(quiz.id) +
            '"><span>' +
            (completed ? "Take again" : "Begin quiz") +
            "</span><b>→</b></button>" +
            "</article>"
          );
        })
        .join("");

      return (
        '<div class="sq-home">' +
        memberBar() +
        '<section class="sq-hero">' +
        symbolRing() +
        '<div class="sq-hero__copy"><p class="sq-eyebrow sq-eyebrow--light">The Sensorium quiz collection</p><h1>' +
        escapeHtml(heading.replace(/\.$/, "")) +
        '<em>.</em></h1><p class="sq-hero__lede">' +
        escapeHtml(subheading) +
        '</p><button class="sq-button sq-button--gold" type="button" data-sq-action="open" data-quiz-id="sensory-signature"><span>Begin with your sensory signature</span><b>↗</b></button><small>5 questions · About 3 minutes · No sign-up</small></div>' +
        '<aside class="sq-hero__note"><span>01—12</span><p>Twelve doorways into a more embodied life.</p></aside>' +
        "</section>" +
        '<div class="sq-marquee"><div>' +
        marquee +
        "</div></div>" +
        '<section class="sq-philosophy"><div class="sq-section-number">01</div><div><p class="sq-eyebrow">A free self-discovery library</p><h2>Your senses are always speaking.</h2><p class="sq-large-copy">These quizzes help you notice what restores you, what your daily rituals are feeding, and which sensory doorway has been waiting for your attention.</p></div><aside><small>The Sensorium principle</small><p>Wellbeing deepens when sight, sound, smell, taste, touch, and intuition are invited into the same room.</p></aside></section>' +
        '<section class="sq-library" id="sensorium-quiz-library"><div class="sq-library__heading"><div><p class="sq-eyebrow">The quiz collection</p><h2>Choose your doorway.</h2></div><p>Twelve complimentary reflections. Five questions each. One simple ritual to bring your result into daily life.</p></div>' +
        '<div class="sq-filters" role="group" aria-label="Filter quizzes">' +
        categories
          .map(function (category) {
            return (
              '<button type="button" data-sq-action="filter" data-category="' +
              escapeHtml(category) +
              '" class="' +
              (state.category === category ? "is-active" : "") +
              '">' +
              escapeHtml(category) +
              "</button>"
            );
          })
          .join("") +
        '</div><div class="sq-grid">' +
        cards +
        "</div></section>" +
        '<section class="sq-how"><div><p class="sq-eyebrow">Your result is an invitation</p><h2>Notice. Understand. Ritualize.</h2></div><div class="sq-how__steps"><article><span>01</span><h3>Answer instinctively</h3><p>Choose what feels true today—not the answer you think you should give.</p></article><article><span>02</span><h3>Read your pattern</h3><p>See the sense that leads and the quieter channels ready for attention.</p></article><article><span>03</span><h3>Try one ritual</h3><p>Turn insight into a small embodied act you can experience immediately.</p></article></div><small>These reflective quizzes are educational tools for self-inquiry, not medical or psychological assessments.</small></section>' +
        '<section class="sq-closing"><div class="sq-closing__symbols">' +
        senseOrder
          .map(function (sense) {
            return "<span>" + senses[sense].icon + "</span>";
          })
          .join("") +
        '</div><p class="sq-eyebrow sq-eyebrow--light">A gentler way of knowing yourself</p><h2>What changes when you listen with all six senses?</h2><button class="sq-button sq-button--gold" type="button" data-sq-action="open" data-quiz-id="sensory-signature"><span>Discover your sensory signature</span><b>↗</b></button></section>' +
        "</div>"
      );
    }

    function introView() {
      var quiz = state.activeQuiz;
      return (
        '<section class="sq-intro"><div class="sq-intro__art" aria-hidden="true"><div class="sq-intro__rings"></div>' +
        senseOrder
          .map(function (sense, index) {
            return (
              '<span class="sq-intro__sense sq-intro__sense--' +
              (index + 1) +
              '">' +
              senses[sense].icon +
              "</span>"
            );
          })
          .join("") +
        "<b>" +
        quiz.number +
        '</b></div><div class="sq-intro__copy"><p class="sq-eyebrow">Complimentary Sensorium reflection</p><h1>' +
        escapeHtml(quiz.title) +
        '</h1><p class="sq-intro__description">' +
        escapeHtml(quiz.description) +
        '</p><div class="sq-intro__details"><span>05 questions</span><span>About 3 minutes</span><span>Personal ritual included</span></div><button class="sq-button sq-button--dark" type="button" data-sq-action="begin"><span>Begin the reflection</span><b>→</b></button><button class="sq-text-button" type="button" data-sq-action="home">Choose another quiz</button></div></section>'
      );
    }

    function questionView() {
      var quiz = state.activeQuiz;
      var question = quiz.questions[state.questionIndex];
      var selected = state.answers[state.questionIndex];
      var progress = ((state.questionIndex + 1) / quiz.questions.length) * 100;
      var answers = question.options
        .map(function (option, index) {
          var isSelected = selected === option.sense;
          return (
            '<button type="button" role="radio" aria-checked="' +
            isSelected +
            '" class="' +
            (isSelected ? "is-selected" : "") +
            '" data-sq-action="answer" data-sense="' +
            option.sense +
            '"><span class="sq-answer__letter">' +
            String.fromCharCode(65 + index) +
            "</span><span>" +
            escapeHtml(option.label) +
            "</span><i>" +
            (isSelected ? "●" : "○") +
            "</i></button>"
          );
        })
        .join("");

      return (
        '<section class="sq-question"><aside class="sq-question__side"><p class="sq-eyebrow sq-eyebrow--light">' +
        escapeHtml(quiz.shortTitle) +
        '</p><div class="sq-question__count"><strong>' +
        String(state.questionIndex + 1).padStart(2, "0") +
        "</strong><span>/ " +
        String(quiz.questions.length).padStart(2, "0") +
        '</span></div><div class="sq-progress"><span style="--sq-progress:' +
        progress +
        '%"></span></div><p>Choose the answer that feels most true in this season of your life.</p></aside>' +
        '<div class="sq-question__panel"><div class="sq-question__heading"><p>' +
        escapeHtml(question.kicker) +
        "</p><h1>" +
        escapeHtml(question.prompt) +
        '</h1></div><div class="sq-answers" role="radiogroup" aria-label="' +
        escapeHtml(question.prompt) +
        '">' +
        answers +
        '</div><div class="sq-question__actions"><button class="sq-back" type="button" data-sq-action="previous"' +
        (state.questionIndex === 0 ? " disabled" : "") +
        '>← Previous</button><button class="sq-button sq-button--dark" type="button" data-sq-action="next"' +
        (selected ? "" : " disabled") +
        "><span>" +
        (state.questionIndex === quiz.questions.length - 1
          ? "Reveal my result"
          : "Next question") +
        "</span><b>→</b></button></div></div></section>"
      );
    }

    function resultView() {
      var quiz = state.activeQuiz;
      var result = resultFor(state.answers);
      var profile = senses[result.primary];
      var maxScore = Math.max.apply(
        null,
        senseOrder.map(function (sense) {
          return result.scores[sense];
        }).concat([1]),
      );
      var quietNames = result.quiet
        .map(function (sense) {
          return senses[sense].name;
        })
        .join(" · ");

      var scoreRows = senseOrder
        .map(function (sense) {
          var width = Math.max(6, (result.scores[sense] / maxScore) * 100);
          return (
            '<div class="sq-score__row"><div><span>' +
            senses[sense].icon +
            "</span><b>" +
            senses[sense].name +
            '</b></div><div class="sq-score__track"><span style="width:' +
            width +
            "%;background:" +
            senses[sense].color +
            '"></span></div><small>' +
            result.scores[sense] +
            "</small></div>"
          );
        })
        .join("");

      var ritual = profile.ritual
        .map(function (step, index) {
          return (
            "<article><span>0" +
            (index + 1) +
            "</span><p>" +
            escapeHtml(step) +
            "</p></article>"
          );
        })
        .join("");

      var savePanel;
      if (state.membership.status === "member") {
        savePanel =
          '<aside class="sq-save-panel sq-save-panel--member"><div><span>' +
          (state.saveStatus === "saved" ? "Saved ✓" : "Your member library") +
          "</span><h3>" +
          escapeHtml(state.saveMessage || "Your complete result will be kept with your account.") +
          '</h3></div><div><a href="' +
          escapeHtml(memberAreaUrl) +
          '">Review saved results →</a>' +
          (state.saveStatus === "error"
            ? '<button type="button" data-sq-action="retry-save">Try saving again</button>'
            : "") +
          "</div></aside>";
      } else {
        savePanel =
          '<aside class="sq-save-panel"><div><span>Keep this reflection</span><h3>' +
          escapeHtml(
            state.saveMessage ||
              "Join free to save this result, every answer you selected, and your ritual.",
          ) +
          '</h3></div><div><a class="sq-member-link sq-member-link--primary" href="' +
          escapeHtml(joinUrl) +
          '" target="_blank" rel="noopener">Join free &amp; save</a><a class="sq-member-link" href="' +
          escapeHtml(loginUrl) +
          '" target="_blank" rel="noopener">Already a member? Sign in</a></div></aside>';
      }

      return (
        '<section class="sq-result" style="--sq-result:' +
        profile.color +
        ';--sq-result-pale:' +
        profile.pale +
        '"><div class="sq-result__hero"><div class="sq-result__kicker"><span>Your result</span><span>' +
        escapeHtml(quiz.shortTitle) +
        '</span></div><div class="sq-result__main"><div class="sq-result__symbol">' +
        profile.icon +
        "</div><div><p>" +
        escapeHtml(quiz.primaryLabel) +
        "</p><h1>" +
        profile.name +
        "</h1><h2>" +
        escapeHtml(profile.essence) +
        '</h2></div></div><p class="sq-result__intro">' +
        escapeHtml(quiz.resultIntros[result.primary]) +
        '</p></div><div class="sq-result__body"><div class="sq-reading"><p class="sq-eyebrow">Your sensory reading</p><h2>This is how you return.</h2><p class="sq-large-copy">' +
        escapeHtml(profile.description) +
        '</p><div class="sq-watch"><small>The gentle watch-out</small><p>' +
        escapeHtml(profile.watchFor) +
        '</p></div></div><aside class="sq-score"><p class="sq-eyebrow">Your six-sense pattern</p><div class="sq-score__list">' +
        scoreRows +
        '</div><div class="sq-quiet"><small>' +
        escapeHtml(quiz.secondaryLabel) +
        "</small><strong>" +
        quietNames +
        "</strong><p>A low score is not a weakness. It is simply a doorway your answers left open for future attention.</p></div></aside></div>" +
        '<div class="sq-ritual"><div><p class="sq-eyebrow">Your five-minute Sensorium ritual</p><h2>A small return through ' +
        profile.name.toLowerCase() +
        '.</h2></div><div class="sq-ritual__steps">' +
        ritual +
        '</div><blockquote>“' +
        escapeHtml(profile.affirmation) +
        '”</blockquote></div>' +
        savePanel +
        '<div class="sq-result__actions"><div><p class="sq-eyebrow sq-eyebrow--light">Continue your sensory education</p><h2>One sense opens the door. All six make a life.</h2></div><div><button class="sq-button sq-button--gold" type="button" data-sq-action="home"><span>Explore another quiz</span><b>↗</b></button><button class="sq-outline" type="button" data-sq-action="share">' +
        state.shareLabel +
        '</button><button class="sq-text-button sq-text-button--light" type="button" data-sq-action="retake">Retake this reflection</button></div></div></section>'
      );
    }

    function render() {
      if (state.phase === "home") root.innerHTML = homeView();
      if (state.phase === "intro") root.innerHTML = introView();
      if (state.phase === "question") root.innerHTML = questionView();
      if (state.phase === "result") root.innerHTML = resultView();
    }

    root.addEventListener("click", function (event) {
      var target = event.target.closest("[data-sq-action]");
      if (!target) return;
      var action = target.dataset.sqAction;

      if (action === "open") {
        openQuiz(target.dataset.quizId);
        return;
      }

      if (action === "filter") {
        state.category = target.dataset.category;
        render();
        var library = root.querySelector("#sensorium-quiz-library");
        if (library) library.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (action === "home") {
        goHome();
        return;
      }

      if (action === "begin") {
        state.phase = "question";
        render();
        goToTop(true);
        return;
      }

      if (action === "answer") {
        state.answers[state.questionIndex] = target.dataset.sense;
        render();
        var next = root.querySelector('[data-sq-action="next"]');
        if (next) next.focus();
        return;
      }

      if (action === "previous" && state.questionIndex > 0) {
        state.questionIndex -= 1;
        render();
        goToTop(false);
        return;
      }

      if (action === "next") {
        if (!state.answers[state.questionIndex]) return;
        if (state.questionIndex < state.activeQuiz.questions.length - 1) {
          state.questionIndex += 1;
          render();
          goToTop(false);
          return;
        }

        state.phase = "result";
        if (state.completed.indexOf(state.activeQuiz.id) === -1) {
          state.completed.push(state.activeQuiz.id);
          writeCompleted(state.completed);
        }
        state.currentResultRecord = buildResultRecord();
        render();
        goToTop(true);
        saveCurrentResult();
        return;
      }

      if (action === "retake") {
        openQuiz(state.activeQuiz.id);
        return;
      }

      if (action === "share") {
        var result = resultFor(state.answers);
        var profile = senses[result.primary];
        var shareText =
          "My " +
          state.activeQuiz.shortTitle +
          " result is " +
          profile.name +
          ": " +
          profile.essence +
          ". Discover your six-sense profile with Sensorium.";

        if (navigator.share) {
          navigator
            .share({ title: state.activeQuiz.title, text: shareText, url: window.location.href })
            .then(function () {
              state.shareLabel = "Shared";
              render();
            })
            .catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(shareText + " " + window.location.href).then(function () {
            state.shareLabel = "Result copied";
            render();
          });
        }
        return;
      }

      if (action === "retry-save") {
        saveCurrentResult();
      }
    });

    render();
    refreshMembership();

    window.addEventListener("focus", refreshMembership);
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "visible") refreshMembership();
    });
  }

  document.querySelectorAll("[data-sensorium-quizzes]").forEach(initialize);

  document.addEventListener("shopify:section:load", function (event) {
    initialize(event.target.querySelector("[data-sensorium-quizzes]"));
  });
})();
